import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { X, Save, Clock, Timer, Trash2, ChevronUp, ChevronDown, Pencil, Copy, Clipboard, AlertTriangle, Check, Percent, Sliders, Target, Info } from "lucide-react";
import SessionTimeline from "./SessionTimeline";
import { uid } from "./uid";
import { getBlockStyle } from "./TimelineBlock";
import { apiMatchProtocolPlayer } from "../../pages/predictiveApi";
import MatchConfirmationModal from "./MatchConfirmationModal";

function normalizeBlocksFromApi(sessionTemplate) {
  return (sessionTemplate?.blocks || []).map((b) => ({
    uid: uid("b_"),
    id: b.id || null,
    protocol_template_id: b.protocol_template_id,
    startMin: b.start_min ?? 0,
    durationMin: b.duration_min ?? 10,
    restDurationMin: b.rest_duration_min ?? 0,
    intensity: b.intensity ?? null,
    label: b.label ?? (b.protocol?.name || "Block"),
    overrides: b.overrides || {},
    sort_order: b.sort_order ?? 0,
    protocol: b.protocol || null,
    matched_games: b.matched_games || {},
    confirmed: b.confirmed || false,
  }));
}

function normalizeSequentialBlocks(blocks) {
  const sorted = [...blocks].sort((a, b) => (a.startMin || 0) - (b.startMin || 0));
  let cursor = 0;
  return sorted.map((b, i) => {
    const isLast = i === sorted.length - 1;
    const rest = isLast ? 0 : (b.restDurationMin || 0);
    const next = { ...b, startMin: cursor, restDurationMin: rest };
    cursor += Math.max(1, b.durationMin || 0) + rest;
    return next;
  });
}

function UnsavedChangesModal({ isDarkMode, onSave, onDiscard, onCancel }) {
  const shell = isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200";
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
      <div className={`relative w-full max-w-md rounded-2xl border ${shell} shadow-2xl p-6`}>
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl ${isDarkMode ? "bg-amber-500/20" : "bg-amber-100"}`}>
            <AlertTriangle className="text-amber-500" size={24} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold">Unsaved Changes</h3>
            <p className={`mt-1 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
              You have unsaved changes. What would you like to do?
            </p>
          </div>
        </div>
        
        <div className="mt-6 flex gap-3 justify-end">
          <button
            onClick={onDiscard}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
              isDarkMode 
                ? "bg-gray-800 hover:bg-gray-700 text-gray-300" 
                : "bg-gray-100 hover:bg-gray-200 text-gray-700"
            }`}
          >
            Discard
          </button>
          <button
            onClick={onCancel}
            className={`px-4 py-2 rounded-xl border text-sm font-medium transition ${
              isDarkMode 
                ? "border-gray-700 hover:bg-gray-800" 
                : "border-gray-200 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 rounded-xl text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function ValidationErrorModal({ isDarkMode, error, onClose }) {
  if (!error) return null;

  const shell = isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200";

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-md rounded-2xl border ${shell} shadow-2xl p-6`}>
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl ${isDarkMode ? "bg-red-500/20" : "bg-red-100"}`}>
            <AlertTriangle className="text-red-500" size={24} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold">Invalid value</h3>
            <p className={`mt-1 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
              {error.message}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}


function BlockEditorPanel({ block, isDarkMode, onChange, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown, globalRefs, refsLoading, matchingLoading, onLaunchMatch, isLast = false }) {
  if (!block) return null;

  const style = getBlockStyle(block.label);
  const IconComponent = style.icon;
  const muted = isDarkMode ? "text-gray-400" : "text-gray-600";
  const inputClass = isDarkMode
    ? "bg-gray-800 border-gray-700 text-gray-100 focus:border-emerald-500"
    : "bg-white border-gray-200 text-gray-900 focus:border-emerald-500";

  const overrides = block.overrides || {};
  const mode = overrides._mode || "global"; // "global" | "per_metric"
  const globalPct = overrides._global_percentage ?? 100;
  const perMetricPcts = overrides._percentages || {};

  const METRIC_KEYS = ["distance", "duration", "hsr", "msr", "sprint80_number"];
  const METRIC_LABELS = {
    distance: "Distance (m)",
    duration: "Duration (min)",
    hsr: "HSR (m)",
    msr: "MSR (m)",
    sprint80_number: "Sprints",
  };

  const setOverrideField = (key, value) => {
    const next = { ...(block.overrides || {}), [key]: value };
    onChange({ ...block, overrides: next });
  };

  const setMode = (newMode) => setOverrideField("_mode", newMode);
  
  const setGlobalPct = (pct) => {
    setOverrideField("_global_percentage", pct);
    if (globalRefs && globalRefs.duration) {
      const newDuration = Math.round(globalRefs.duration * pct / 100);
      onChange({ ...block, durationMin: Math.max(0, newDuration), overrides: { ...(block.overrides || {}), _global_percentage: pct } });
      return;
    }
  };
  
  const setMetricPct = (metric, pct) => {
    const next = { ...(perMetricPcts), [metric]: pct };
    setOverrideField("_percentages", next);
    
    if (metric === "duration" && globalRefs && globalRefs.duration) {
      const newDuration = Math.round(globalRefs.duration * pct / 100);
      onChange({ 
        ...block, 
        durationMin: Math.max(0, newDuration), 
        overrides: { ...(block.overrides || {}), _percentages: next } 
      });
      return;
    }
  };

  const isConfirmed = block.confirmed || false;

  return (
    <div className={`rounded-xl border p-4 ${isDarkMode ? "bg-gray-800/50 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-xl ${isDarkMode ? style.bgDark : style.bgLight}`}>
          <IconComponent size={20} className="text-white" />
        </div>

        <div className="flex-1 min-w-0 space-y-4">
          {/* Block name (read-only) */}
          <div>
            <label className={`text-xs font-medium ${muted}`}>Block</label>
            <p className="mt-1 text-sm font-semibold">{block.label || "Block"}</p>
          </div>

          {/* Rest Duration — hidden for the last block (rest after last block is meaningless) */}
          {!isLast && (
            <div>
              <label className={`text-xs font-medium ${muted}`}>Rest after block (min)</label>
              <input
                type="number"
                min={0}
                max={180}
                value={block.restDurationMin || 0}
                onChange={(e) => onChange({ ...block, restDurationMin: Math.max(0, Number(e.target.value) || 0) })}
                className={`w-full mt-1 rounded-lg border px-3 py-2 text-sm outline-none ${inputClass}`}
              />
            </div>
          )}

          {/* Confirmation badge */}
          {isConfirmed && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 text-xs font-medium">
              <Check size={14} />
              Block confirmed · {Object.keys(block.matched_games || {}).length} players matched
            </div>
          )}

          {/* Mode selection */}
          <div>
            <label className={`text-xs font-semibold ${muted} mb-2 block`}>Calculation method</label>
            <div className="flex gap-2">
              <button
                onClick={() => setMode("global")}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition border ${
                  mode === "global"
                    ? "bg-emerald-600 text-white border-emerald-500 shadow-md"
                    : isDarkMode
                      ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"
                      : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Percent size={14} />
                Global %
              </button>
              <button
                onClick={() => setMode("per_metric")}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition border ${
                  mode === "per_metric"
                    ? "bg-emerald-600 text-white border-emerald-500 shadow-md"
                    : isDarkMode
                      ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"
                      : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Sliders size={14} />
                % Per metric
              </button>
            </div>
          </div>

          {/* Global percentage mode */}
          {mode === "global" && (
            <div>
              <label className={`text-xs font-medium ${muted}`}>Global percentage</label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="range"
                  min={0}
                  max={120}
                  step={5}
                  value={globalPct}
                  onChange={(e) => setGlobalPct(Number(e.target.value))}
                  className="flex-1 accent-emerald-500"
                />
                <input
                  type="number"
                  min={0}
                  max={120}
                  value={globalPct}
                  onChange={(e) => setGlobalPct(Number(e.target.value) || 0)}
                  className={`w-20 rounded-lg border px-3 py-2 text-sm text-center outline-none ${inputClass}`}
                />
                <span className={`text-sm font-medium ${muted}`}>%</span>
              </div>
              {refsLoading ? (
                <div className="grid grid-cols-5 gap-2 mt-3">
                  {METRIC_KEYS.map((k) => (
                    <div key={k} className={`rounded-lg border p-2 text-center ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                      <p className={`text-[10px] ${muted}`}>{METRIC_LABELS[k]}</p>
                      <div className={`mx-auto mt-0.5 h-3.5 w-8 rounded ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} animate-pulse`} />
                    </div>
                  ))}
                </div>
              ) : globalRefs ? (
                <div className="grid grid-cols-5 gap-2 mt-3">
                  {METRIC_KEYS.map((k) => {
                    const ref = globalRefs[k] || 0;
                    const val = Math.round(ref * globalPct / 100 * 100) / 100;
                    return (
                      <div key={k} className={`rounded-lg border p-2 text-center ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                        <p className={`text-[10px] ${muted}`}>{METRIC_LABELS[k]}</p>
                        <p className="text-xs font-semibold text-emerald-500">{val}</p>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          )}

          {/* Per-metric percentage mode */}
          {mode === "per_metric" && (
            <div className="space-y-3">
              <label className={`text-xs font-semibold ${muted}`}>Percentages per metric</label>
              {METRIC_KEYS.map((k) => {
                let pct = perMetricPcts[k] ?? 100;
                
                if (k === "duration" && globalRefs?.duration > 0) {
                  const calculatedPct = Math.round((block.durationMin / globalRefs.duration) * 100);
                  if (Math.abs(calculatedPct - pct) > 1) {
                    pct = calculatedPct;
                    setOverrideField("_percentages", { ...perMetricPcts, duration: calculatedPct });
                  }
                }
                
                const ref = globalRefs?.[k] || 0;
                const val = Math.round(ref * pct / 100 * 100) / 100;
                
                const minPct = k === "duration" ? 0 : 10;
                const maxPct = k === "duration" ? 120 : 100;
                
                return (
                  <div key={k} className="flex items-center gap-3">
                    <div className="w-24">
                      <p className="text-xs font-medium">{METRIC_LABELS[k]}</p>
                      {refsLoading ? (
                        <div className={`mt-0.5 h-3 w-14 rounded ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} animate-pulse`} />
                      ) : (
                        <p className={`text-[10px] ${muted}`}>Ref: {ref}</p>
                      )}
                    </div>
                    <input
                      type="range"
                      min={minPct}
                      max={maxPct}
                      step={5}
                      value={pct}
                      onChange={(e) => setMetricPct(k, Number(e.target.value))}
                      className="flex-1 accent-emerald-500"
                    />
                    <input
                      type="number"
                      min={minPct}
                      max={maxPct}
                      value={pct}
                      onChange={(e) => setMetricPct(k, Number(e.target.value) || 0)}
                      className={`w-16 rounded-lg border px-2 py-1.5 text-xs text-center outline-none ${inputClass}`}
                    />
                    <span className={`text-xs ${muted} w-6`}>%</span>
                    <div className="w-16 text-right">
                      {refsLoading ? (
                        <div className={`ml-auto h-3.5 w-10 rounded ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} animate-pulse`} />
                      ) : (
                        <span className="text-xs font-semibold text-emerald-500">{val}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Launch match button */}
          <div className="pt-2">
            <button
              onClick={() => onLaunchMatch?.(block)}
              disabled={refsLoading || matchingLoading}
              className={`w-full flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                refsLoading || matchingLoading
                  ? "bg-gray-600 text-gray-400 cursor-not-allowed opacity-60"
                  : isConfirmed
                    ? isDarkMode
                      ? "bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30"
                      : "bg-emerald-50 hover:bg-emerald-100 text-emerald-600 border border-emerald-200"
                    : "bg-blue-600 hover:bg-blue-700 text-white shadow-md"
              }`}
            >
              {refsLoading ? (
                <>
                  <div className="h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  Loading references…
                </>
              ) : matchingLoading ? (
                <>
                  <div className="h-4 w-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  Matching in progress…
                </>
              ) : (
                <>
                  <Target size={16} />
                  {isConfirmed ? "Recalculate matching" : "Launch player matching"}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1">
          <button onClick={onMoveUp} disabled={!canMoveUp} className={`p-1.5 rounded-lg border transition ${
            canMoveUp ? (isDarkMode ? "border-gray-700 hover:bg-gray-700" : "border-gray-200 hover:bg-gray-100")
                     : "opacity-30 cursor-not-allowed border-transparent"
          }`}>
            <ChevronUp size={14} />
          </button>

          <button onClick={onMoveDown} disabled={!canMoveDown} className={`p-1.5 rounded-lg border transition ${
            canMoveDown ? (isDarkMode ? "border-gray-700 hover:bg-gray-700" : "border-gray-200 hover:bg-gray-100")
                       : "opacity-30 cursor-not-allowed border-transparent"
          }`}>
            <ChevronDown size={14} />
          </button>

          <button onClick={onRemove} className={`p-1.5 rounded-lg border text-red-500 transition ${
            isDarkMode ? "border-gray-700 hover:bg-red-500/10" : "border-gray-200 hover:bg-red-50"
          }`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}


export default function SessionEditorModal({
  isDarkMode,
  open,
  onClose,
  sessionTemplate,
  protocolTemplates,
  onSave,
  playerReferences,
  refsLoading,
}) {
  const shell = isDarkMode ? "bg-gray-950 border-gray-700" : "bg-white border-gray-200";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-600";
  const inputClass = isDarkMode 
    ? "bg-gray-800 border-gray-700 text-gray-100 focus:border-emerald-500" 
    : "bg-white border-gray-200 text-gray-900 focus:border-emerald-500";

  const modalRef = useRef(null);
  const nameInputRef = useRef(null);
  
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [selectedBlockUids, setSelectedBlockUids] = useState(new Set());
  const [clipboard, setClipboard] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [shakeModal, setShakeModal] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [matchedGame, setMatchedGame] = useState(null);
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [matchingError, setMatchingError] = useState(null);
  const [matchPreviewData, setMatchPreviewData] = useState(null);
  const [matchPreviewBlock, setMatchPreviewBlock] = useState(null);
  const [matchConfirmLoading, setMatchConfirmLoading] = useState(false);

  // Info panel
  const [showInfoPanel, setShowInfoPanel] = useState(false);

  const globalRefs = useMemo(
    () => playerReferences?.global || null,
    [playerReferences]
  );
  
  const initialStateRef = useRef({ name: "", description: "", blocks: [] });

  const checkForChanges = useCallback(() => {
    const initial = initialStateRef.current;
    const nameChanged = name !== initial.name;
    const descChanged = description !== initial.description;
    const blocksChanged = JSON.stringify(blocks.map(b => ({ label: b.label, durationMin: b.durationMin, intensity: b.intensity }))) 
      !== JSON.stringify(initial.blocks.map(b => ({ label: b.label, durationMin: b.durationMin, intensity: b.intensity })));
    return nameChanged || descChanged || blocksChanged;
  }, [name, description, blocks]);

  useEffect(() => {
    if (!open) return;
    const initialBlocks = normalizeBlocksFromApi(sessionTemplate);
    setName(sessionTemplate?.name || "");
    setDescription(sessionTemplate?.description || "");
    setBlocks(initialBlocks);
    setSelectedBlockUids(new Set());
    setClipboard([]);
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    setNameError(false);
    setSaveSuccess(false);
    setValidationError(null);
    setMatchedGame(null);
    setMatchingLoading(false);
    setMatchingError(null);
    setMatchPreviewData(null);
    setMatchPreviewBlock(null);
    setMatchConfirmLoading(false);
    setShowInfoPanel(false);
    
    initialStateRef.current = {
      name: sessionTemplate?.name || "",
      description: sessionTemplate?.description || "",
      blocks: initialBlocks,
    };
  }, [open, sessionTemplate]);

  useEffect(() => {
    if (open) {
      setHasUnsavedChanges(checkForChanges());
    }
  }, [name, description, blocks, open, checkForChanges]);

  const totalDuration = useMemo(
    () => blocks.reduce((s, b) => s + (b.durationMin || 0) + (b.restDurationMin || 0), 0),
    [blocks]
  );

  const selectedBlockUid = useMemo(
    () => selectedBlockUids.size === 1 ? Array.from(selectedBlockUids)[0] : null,
    [selectedBlockUids]
  );

  const selectedBlock = useMemo(
    () => selectedBlockUid ? blocks.find((b) => b.uid === selectedBlockUid) : null,
    [blocks, selectedBlockUid]
  );

  const selectedBlockIndex = useMemo(
    () => selectedBlockUid ? blocks.findIndex((b) => b.uid === selectedBlockUid) : -1,
    [blocks, selectedBlockUid]
  );

  const handleSelectBlock = useCallback((blockUid, shiftKey = false) => {
    if (!blockUid) {
      setSelectedBlockUids(new Set());
      return;
    }
    
    if (shiftKey) {
      setSelectedBlockUids(prev => {
        const next = new Set(prev);
        if (next.has(blockUid)) {
          next.delete(blockUid);
        } else {
          next.add(blockUid);
        }
        return next;
      });
    } else {
      setSelectedBlockUids(new Set([blockUid]));
    }
  }, []);

  const updateBlock = (updatedBlock) => {
    setBlocks((prev) => {
      const next = prev.map((b) => (b.uid === updatedBlock.uid ? updatedBlock : b));
      return normalizeSequentialBlocks(next);
    });
  };

  const removeBlock = (uidToRemove) => {
    setBlocks((prev) => {
      const next = prev.filter((b) => b.uid !== uidToRemove);
      return normalizeSequentialBlocks(next);
    });
    setSelectedBlockUids(prev => {
      const next = new Set(prev);
      next.delete(uidToRemove);
      return next;
    });
  };

  const removeSelectedBlocks = useCallback(() => {
    if (selectedBlockUids.size === 0) return;
    setBlocks((prev) => {
      const next = prev.filter((b) => !selectedBlockUids.has(b.uid));
      return normalizeSequentialBlocks(next);
    });
    setSelectedBlockUids(new Set());
  }, [selectedBlockUids]);

  const copySelectedBlocks = useCallback(() => {
    if (selectedBlockUids.size === 0) return;
    const blocksToCopy = blocks
      .filter(b => selectedBlockUids.has(b.uid))
      .map(b => ({ ...b }));
    setClipboard(blocksToCopy);
  }, [blocks, selectedBlockUids]);

  const pasteBlocks = useCallback(() => {
    if (clipboard.length === 0) return;
    const newBlocks = clipboard.map(b => ({
      ...b,
      uid: uid("b_"),
      id: null,
    }));
    setBlocks(prev => normalizeSequentialBlocks([...prev, ...newBlocks]));
    setSelectedBlockUids(new Set(newBlocks.map(b => b.uid)));
  }, [clipboard]);

  const moveBlock = (uidToMove, direction) => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.uid === uidToMove);
      if (idx < 0) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      
      return normalizeSequentialBlocks(next);
    });
  };

  const triggerShake = useCallback(() => {
    setShakeModal(true);
    setNameError(true);
    nameInputRef.current?.focus();
    setTimeout(() => setShakeModal(false), 500);
    setTimeout(() => setNameError(false), 3000);
  }, []);

  const launchBlockMatch = useCallback(async (block) => {
    if (!block) {
      setValidationError({ message: "Select a block to launch matching." });
      return;
    }

    const overrides = block.overrides || {};
    const mode = overrides._mode || "global";
    const payload = {
      mode,
      block_label: block.label || "Block",
    };

    if (mode === "per_metric") {
      payload.percentages = overrides._percentages || {};
    } else {
      payload.global_percentage = overrides._global_percentage ?? 100;
    }

    setMatchingLoading(true);
    setMatchingError(null);
    setMatchPreviewBlock(block.uid);

    try {
      const json = await apiMatchProtocolPlayer(payload);

      if (!json || json.error) {
        setMatchingError(json?.message || "Matching failed.");
        setMatchPreviewData(null);
        return;
      }

      setMatchPreviewData(json);
    } catch (e) {
      setMatchingError(String(e?.message || e));
      setMatchPreviewData(null);
    } finally {
      setMatchingLoading(false);
    }
  }, []);

  const handleConfirmMatch = useCallback((matchData) => {
    if (!matchPreviewBlock) return;

    const matchedGames = {};
    for (const p of (matchData.players || [])) {
      if (p.matched_game) {
        matchedGames[p.player_id] = {
          protocol_player_id: p.matched_game.protocol_player_id,
          target_metrics: p.target_metrics || {},
          matched_name: p.matched_game.name || null,
        };
      }
    }

    setBlocks((prev) =>
      prev.map((b) => {
        if (b.uid !== matchPreviewBlock) return b;
        return { ...b, matched_games: matchedGames, confirmed: true };
      })
    );

    setMatchPreviewData(null);
    setMatchPreviewBlock(null);
  }, [matchPreviewBlock]);

  const validateBlocks = useCallback((blocksToValidate) => {
    const NON_NEGATIVE_KEYS = ["distance", "sprint80_number", "msr", "hsr"];

    for (let i = 0; i < blocksToValidate.length; i++) {
      const b = blocksToValidate[i];
      const overrides = b.overrides || {};

      for (const key of NON_NEGATIVE_KEYS) {
        if (overrides[key] === undefined || overrides[key] === null) continue;

        const n = Number(overrides[key]);
        if (!Number.isFinite(n)) continue;

        if (n < 0) {
          const label = b.label || `Block ${i + 1}`;
          const prettyKey =
            key === "distance" ? "Distance" :
            key === "sprint80_number" ? "Number Sprint" :
            key.toUpperCase();

          return {
            blockUid: b.uid,
            message: `Block "${label}": "${prettyKey}" cannot be negative (got ${n}).`,
          };
        }
      }
    }

    return null;
  }, []);

  const save = useCallback(async () => {
    if (!name.trim()) {
      triggerShake();
      return;
    }

    const unconfirmedBlocks = blocks.filter((b) => !b.confirmed);
    if (unconfirmedBlocks.length > 0) {
      const firstLabel = unconfirmedBlocks[0].label || "Block";
      setValidationError({
        blockUid: unconfirmedBlocks[0].uid,
        message: `Block "${firstLabel}" has not been confirmed. Run matching and confirm each block before saving.`,
      });
      setSelectedBlockUids(new Set([unconfirmedBlocks[0].uid]));
      setShakeModal(true);
      setTimeout(() => setShakeModal(false), 500);
      return;
    }

    const invalid = validateBlocks(blocks);
    if (invalid) {
      setValidationError(invalid);
      setSelectedBlockUids(new Set([invalid.blockUid]));
      setShakeModal(true);
      setTimeout(() => setShakeModal(false), 500);
      return;
    }

    const payload = {
      id: sessionTemplate?.id || undefined,
      name: name.trim(),
      description,
      tags: sessionTemplate?.tags || [],
      blocks: blocks.map((b, i) => ({
        id: b.id || undefined,
        protocol_template_id: b.protocol_template_id,
        start_min: Math.round(b.startMin),
        duration_min: Math.round(b.durationMin),
        rest_duration_min: i === blocks.length - 1 ? 0 : Math.round(b.restDurationMin || 0),
        intensity: b.intensity ?? null,
        label: b.label ?? null,
        overrides: b.overrides || {},
        sort_order: i,
        matched_games: b.matched_games || {},
        confirmed: b.confirmed || false,
      })),
    };

    await onSave(payload);
    setHasUnsavedChanges(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
    
    initialStateRef.current = {
      name: name.trim(),
      description,
      blocks: [...blocks],
    };
  }, [name, description, blocks, sessionTemplate, onSave, triggerShake]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        save();
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === "c" && selectedBlockUids.size > 0) {
        if (document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
          e.preventDefault();
          copySelectedBlocks();
        }
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === "v" && clipboard.length > 0) {
        if (document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
          e.preventDefault();
          pasteBlocks();
        }
        return;
      }

      if ((e.key === "Delete" || e.key === "Backspace") && selectedBlockUids.size > 0) {
        if (document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
          e.preventDefault();
          removeSelectedBlocks();
        }
        return;
      }

      if (e.key === "Escape") {
        if (showInfoPanel) {
          setShowInfoPanel(false);
          return;
        }
        if (selectedBlockUids.size > 0) {
          setSelectedBlockUids(new Set());
        } else {
          handleClose();
        }
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, save, copySelectedBlocks, pasteBlocks, removeSelectedBlocks, selectedBlockUids, clipboard, handleClose, showInfoPanel]);

  if (!open) return null;

  const allConfirmed = blocks.length > 0 && blocks.every(b => b.confirmed);
  const notConfirmedCount = blocks.filter(b => !b.confirmed).length;

  return (
    <>
      {showUnsavedModal && (
        <UnsavedChangesModal
          isDarkMode={isDarkMode}
          onSave={async () => {
            await save();
            setShowUnsavedModal(false);
            onClose();
          }}
          onDiscard={() => {
            setShowUnsavedModal(false);
            setHasUnsavedChanges(false);
            onClose();
          }}
          onCancel={() => setShowUnsavedModal(false)}
        />
      )}

      {validationError && (
        <ValidationErrorModal
          isDarkMode={isDarkMode}
          error={validationError}
          onClose={() => setValidationError(null)}
        />
      )}

      {matchPreviewData && (
        <MatchConfirmationModal
          isDarkMode={isDarkMode}
          matchData={matchPreviewData}
          blockLabel={blocks.find((b) => b.uid === matchPreviewBlock)?.label || "Block"}
          onConfirm={handleConfirmMatch}
          onCancel={() => { setMatchPreviewData(null); setMatchPreviewBlock(null); }}
          loading={matchConfirmLoading}
        />
      )}

      <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

        <div
          ref={modalRef}
          className={`relative w-full max-w-6xl max-h-[95vh] rounded-2xl border ${shell} shadow-2xl flex flex-col overflow-hidden transition-transform ${
            shakeModal ? "animate-shake" : ""
          }`}
          style={{
            animation: shakeModal ? "shake 0.5s ease-in-out" : undefined,
          }}
        >
          <style>{`
            @keyframes shake {
              0%, 100% { transform: translateX(0); }
              10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
              20%, 40%, 60%, 80% { transform: translateX(5px); }
            }
            .animate-shake { animation: shake 0.5s ease-in-out; }
          `}</style>

          {/* HEADER */}
          <header className={`flex items-center justify-between gap-4 p-4 border-b ${
            isDarkMode ? "border-gray-800" : "border-gray-200"
          }`}>
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="min-w-0 flex-1">
                <div className="relative">
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={`w-full text-xl font-semibold bg-transparent border-b-2 outline-none pb-1 transition-colors ${
                      nameError
                        ? "border-red-500 text-red-500 placeholder-red-400"
                        : isDarkMode 
                          ? "border-transparent focus:border-emerald-500 text-gray-100 placeholder-gray-600" 
                          : "border-transparent focus:border-emerald-500 text-gray-900 placeholder-gray-400"
                    }`}
                    placeholder={nameError ? "⚠️ Session name required!" : "Session name..."}
                  />
                  {nameError && (
                    <p className="absolute -bottom-5 left-0 text-xs text-red-500 font-medium">
                      Please enter a session name to save
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <div className={`flex items-center gap-1.5 text-sm ${muted}`}>
                    <Clock size={14} />
                    <span>{blocks.length} blocks</span>
                  </div>
                  <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-lg ${
                    isDarkMode ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-500/10 text-emerald-600"
                  }`}>
                    <Timer size={14} />
                    <span className="text-sm font-semibold">{totalDuration} min total</span>
                  </div>
                  {selectedBlockUids.size > 0 && (
                    <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-lg ${
                      isDarkMode ? "bg-blue-500/20 text-blue-400" : "bg-blue-500/10 text-blue-600"
                    }`}>
                      <span className="text-sm font-medium">{selectedBlockUids.size} selected</span>
                    </div>
                  )}
                  {clipboard.length > 0 && (
                    <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-lg ${
                      isDarkMode ? "bg-violet-500/20 text-violet-400" : "bg-violet-500/10 text-violet-600"
                    }`}>
                      <Clipboard size={12} />
                      <span className="text-xs font-medium">{clipboard.length} copied</span>
                    </div>
                  )}
                  {hasUnsavedChanges && (
                    <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-lg ${
                      isDarkMode ? "bg-amber-500/20 text-amber-400" : "bg-amber-500/10 text-amber-600"
                    }`}>
                      <span className="text-xs font-medium">• Unsaved</span>
                    </div>
                  )}
                  {blocks.length > 0 && (
                    <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-lg ${
                      allConfirmed
                        ? isDarkMode ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-500/10 text-emerald-600"
                        : isDarkMode ? "bg-red-500/20 text-red-400" : "bg-red-500/10 text-red-600"
                    }`}>
                      {allConfirmed ? (
                        <>
                          <Check size={12} />
                          <span className="text-xs font-medium">All confirmed</span>
                        </>
                      ) : (
                        <>
                          <AlertTriangle size={12} />
                          <span className="text-xs font-medium">
                            {notConfirmedCount} not confirmed
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowInfoPanel(p => !p)}
                className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition border ${
                  showInfoPanel
                    ? isDarkMode
                      ? "bg-blue-500/20 border-blue-500/30 text-blue-400"
                      : "bg-blue-50 border-blue-200 text-blue-600"
                    : isDarkMode
                      ? "border-gray-700 hover:bg-gray-800 text-gray-400"
                      : "border-gray-200 hover:bg-gray-50 text-gray-600"
                }`}
                title="Add a description to this session"
              >
                <Info size={14} />
                Add Description 
              </button>

              {selectedBlockUids.size > 0 && (
                <button
                  onClick={copySelectedBlocks}
                  className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition ${
                    isDarkMode 
                      ? "bg-gray-800 hover:bg-gray-700 text-gray-300" 
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                  }`}
                  title="Copy selected (Ctrl+C)"
                >
                  <Copy size={14} />
                  Copy
                </button>
              )}

              {clipboard.length > 0 && (
                <button
                  onClick={pasteBlocks}
                  className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition ${
                    isDarkMode 
                      ? "bg-violet-500/20 hover:bg-violet-500/30 text-violet-400" 
                      : "bg-violet-500/10 hover:bg-violet-500/20 text-violet-600"
                  }`}
                  title="Paste (Ctrl+V)"
                >
                  <Clipboard size={14} />
                  Paste
                </button>
              )}

              {selectedBlockUids.size > 0 && (
                <button
                  onClick={removeSelectedBlocks}
                  className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition ${
                    isDarkMode 
                      ? "bg-red-500/20 hover:bg-red-500/30 text-red-400" 
                      : "bg-red-500/10 hover:bg-red-500/20 text-red-600"
                  }`}
                  title="Delete selected (Del)"
                >
                  <Trash2 size={14} />
                  Delete ({selectedBlockUids.size})
                </button>
              )}

              <button
                onClick={save}
                disabled={blocks.length > 0 && !blocks.every(b => b.confirmed)}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-medium transition ${
                  saveSuccess
                    ? "bg-green-600 text-white"
                    : (blocks.length > 0 && !blocks.every(b => b.confirmed))
                      ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                      : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/20"
                }`}
                title={blocks.length > 0 && !blocks.every(b => b.confirmed) ? "Confirm all blocks before saving" : "Save (Ctrl+S)"}
              >
                {saveSuccess ? <Check size={16} /> : <Save size={16} />}
                {saveSuccess ? "Saved!" : "Save"}
              </button>

              <button
                onClick={handleClose}
                className={`rounded-xl border p-2.5 transition ${
                  isDarkMode
                    ? "border-gray-700 hover:bg-gray-800"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
          </header>

          {/* BODY */}
          <div className="flex flex-1 overflow-hidden relative">

            {/* LEFT: BLOCKS LIBRARY */}
            <aside className={`w-64 shrink-0 border-r p-4 overflow-y-auto ${
              isDarkMode ? "border-gray-800 bg-gray-900/50" : "border-gray-200 bg-gray-50/50"
            }`}>
              <h4 className="text-sm font-semibold mb-1">Training Blocks</h4>
              <p className={`text-xs ${muted} mb-3`}>Drag to timeline or click to add</p>

              <div className="space-y-2">
                {(protocolTemplates || []).map((p) => {
                  const style = getBlockStyle(p.name);
                  const IconComponent = style.icon;
                  
                  return (
                    <div
                      key={p.id}
                      draggable
                      onDragStart={(e) =>
                        e.dataTransfer.setData(
                          "application/x-protocol-block",
                          JSON.stringify(p)
                        )
                      }
                      onClick={() => {
                        let startMin = 0;
                        if (blocks && blocks.length > 0) {
                          const lastBlock = blocks[blocks.length - 1];
                          startMin = (lastBlock.startMin || 0) + (lastBlock.durationMin || 0) + (lastBlock.restDurationMin || 0);
                        }
                        
                        const newBlock = {
                          uid: uid("b_"),
                          protocol_template_id: p.id,
                          label: p.name,
                          startMin: startMin,
                          durationMin: p.default_duration_min || 15,
                          restDurationMin: 0,
                          intensity: p.default_intensity || null,
                        };
                        setBlocks((prev) => {
                          const updated = prev.map((b, i) => 
                            i === prev.length - 1 && (b.restDurationMin || 0) === 0
                              ? { ...b, restDurationMin: 5 }
                              : b
                          );
                          return normalizeSequentialBlocks([...updated, newBlock]);
                        });
                        setSelectedBlockUids(new Set([newBlock.uid]));
                      }}
                      className={`flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer transition ${
                        isDarkMode
                          ? "border-gray-700 hover:bg-gray-800 hover:border-gray-600"
                          : "border-gray-200 hover:bg-white hover:border-gray-300"
                      }`}
                    >
                      <div className={`p-1.5 rounded-lg ${isDarkMode ? style.bgDark : style.bgLight}`}>
                        <IconComponent size={14} className="text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium truncate">{p.name}</p>
                        {p.default_duration_min && (
                          <p className={`text-[10px] ${muted}`}>{p.default_duration_min} min</p>
                        )}
                      </div>
                      {/* Tooltip on description in the library sidebar */}
                      {p.description && (
                        <div className="relative group flex-shrink-0">
                          <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold cursor-help border transition
                            ${isDarkMode
                              ? "border-gray-600 text-gray-500 hover:border-blue-400 hover:text-blue-400"
                              : "border-gray-300 text-gray-400 hover:border-blue-400 hover:text-blue-500"
                            }`}>
                            ?
                          </span>
                          <div className={`
                            absolute right-0 bottom-full mb-2 z-50
                            w-56 rounded-xl border px-3 py-2.5 text-xs shadow-xl
                            pointer-events-none
                            opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100
                            transition-all duration-150 origin-bottom-right
                            ${isDarkMode
                              ? "bg-gray-900 border-gray-700 text-gray-200"
                              : "bg-white border-gray-200 text-gray-700"
                            }
                          `}>
                            <p className="font-semibold text-[11px] mb-1">{p.name}</p>
                            <p className="leading-relaxed">{p.description}</p>
                            <div className={`absolute right-2 top-full w-2 h-2 rotate-45 border-r border-b
                              ${isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}
                            `} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {(!protocolTemplates || protocolTemplates.length === 0) && (
                <div className={`text-center py-8 ${muted}`}>
                  <p className="text-sm">No blocks available</p>
                  <p className="text-xs mt-1">Create protocol templates first</p>
                </div>
              )}
            </aside>

            {/* RIGHT: TIMELINE + EDITOR */}
            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
              <div className="p-4 flex-shrink-0">
                <SessionTimeline
                  blocks={blocks}
                  onChange={setBlocks}
                  isDarkMode={isDarkMode}
                  selectedBlockUids={selectedBlockUids}
                  onSelectBlock={handleSelectBlock}
                />
              </div>

              <div className={`flex-1 overflow-y-auto border-t p-4 ${
                isDarkMode ? "border-gray-800 bg-gray-900/30" : "border-gray-200 bg-gray-50/30"
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <Pencil size={14} />
                    {selectedBlock ? "Edit Block" : selectedBlockUids.size > 1 ? `${selectedBlockUids.size} Blocks Selected` : "Session Blocks"}
                  </h4>
                  <div className="flex items-center gap-2">
                    {selectedBlockUids.size > 0 && (
                      <button
                        onClick={() => setSelectedBlockUids(new Set())}
                        className={`text-xs ${muted} hover:underline`}
                      >
                        Clear selection
                      </button>
                    )}
                    <span className={`text-xs ${muted}`}>
                      Shift+click for multi-select
                    </span>
                  </div>
                </div>

                {selectedBlock ? (
                  <BlockEditorPanel
                    block={selectedBlock}
                    isDarkMode={isDarkMode}
                    onChange={updateBlock}
                    onRemove={() => removeBlock(selectedBlock.uid)}
                    onMoveUp={() => moveBlock(selectedBlock.uid, -1)}
                    onMoveDown={() => moveBlock(selectedBlock.uid, 1)}
                    canMoveUp={selectedBlockIndex > 0}
                    canMoveDown={selectedBlockIndex < blocks.length - 1}
                    isLast={selectedBlockIndex === blocks.length - 1}
                    globalRefs={globalRefs}
                    refsLoading={refsLoading}
                    matchingLoading={matchingLoading}
                    onLaunchMatch={launchBlockMatch}
                  />
                ) : (
                  <div className="space-y-2">
                    {blocks.length === 0 ? (
                      <div className={`text-center py-8 ${muted}`}>
                        <p className="text-sm">No blocks yet</p>
                        <p className="text-xs mt-1">Add blocks from the library on the left</p>
                      </div>
                    ) : (
                      (() => {
                        const maxDur = Math.max(...blocks.map(b => (b.durationMin || 0) + (b.restDurationMin || 0)), 1);
                        return blocks.map((b, idx) => {
                          const style = getBlockStyle(b.label);
                          const IconComponent = style.icon;
                          const isSelected = selectedBlockUids.has(b.uid);
                          const isLast = idx === blocks.length - 1;
                          const totalBlockMin = (b.durationMin || 0) + (b.restDurationMin || 0);
                          const barPct = Math.max(5, (totalBlockMin / maxDur) * 100);
                          // description: from protocol object or from overrides
                          const blockDescription = b.protocol?.description || b.overrides?.description || null;
                          
                          return (
                            <div
                              key={b.uid}
                              onClick={(e) => handleSelectBlock(b.uid, e.shiftKey)}
                              className={`relative overflow-hidden rounded-xl border cursor-pointer transition ${
                                isSelected
                                  ? isDarkMode 
                                    ? "border-emerald-500 bg-emerald-500/10 ring-1 ring-emerald-500/50" 
                                    : "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500/50"
                                  : isDarkMode 
                                    ? "border-gray-700 hover:bg-gray-800/50 hover:border-gray-600" 
                                    : "border-gray-200 hover:bg-white hover:border-gray-300"
                              }`}
                            >
                              <div
                                className={`absolute inset-y-0 left-0 transition-all ${
                                  isDarkMode ? style.bgDark : style.bgLight
                                } opacity-10`}
                                style={{ width: `${barPct}%` }}
                              />
                              <div className="relative flex items-center gap-3 p-3">
                                <div className={`text-xs font-bold ${isSelected ? "text-emerald-500" : muted} w-6`}>
                                  {idx + 1}
                                </div>
                                <div className={`p-1.5 rounded-lg ${isDarkMode ? style.bgDark : style.bgLight}`}>
                                  <IconComponent size={14} className="text-white" />
                                </div>

                                {/* ── Block name + tooltip ── */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    <p className="text-sm font-medium truncate">{b.label || "Block"}</p>
                                    {blockDescription && (
                                      <div
                                        className="relative group flex-shrink-0"
                                        onClick={(e) => e.stopPropagation()} // prevent block selection on hover zone
                                      >
                                        <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold cursor-help border transition
                                          ${isDarkMode
                                            ? "border-gray-600 text-gray-500 hover:border-blue-400 hover:text-blue-400"
                                            : "border-gray-300 text-gray-400 hover:border-blue-400 hover:text-blue-500"
                                          }`}>
                                          ?
                                        </span>
                                        {/* Tooltip bubble */}
                                        <div className={`
                                          absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50
                                          w-56 rounded-xl border px-3 py-2.5 text-xs shadow-xl
                                          pointer-events-none
                                          opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100
                                          transition-all duration-150 origin-bottom
                                          ${isDarkMode
                                            ? "bg-gray-900 border-gray-700 text-gray-200"
                                            : "bg-white border-gray-200 text-gray-700"
                                          }
                                        `}>
                                          <p className="font-semibold text-[11px] mb-1 truncate">{b.label || "Block"}</p>
                                          <p className="leading-relaxed whitespace-pre-wrap">{blockDescription}</p>
                                          {/* Arrow */}
                                          <div className={`absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 rotate-45 border-r border-b
                                            ${isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}
                                          `} />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  {!isLast && b.restDurationMin > 0 && (
                                    <p className={`text-[10px] mt-0.5 ${muted}`}>+ {b.restDurationMin} min rest</p>
                                  )}
                                </div>

                                <div className={`flex items-center gap-3 text-xs ${muted}`}>
                                  <span>{b.durationMin} min</span>
                                  {b.confirmed ? (
                                    <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-500 text-[10px] font-medium">
                                      <Check size={10} />
                                      Apply
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/20 text-amber-500 text-[10px] font-medium">
                                      <AlertTriangle size={10} />
                                      Discard
                                    </span>
                                  )}
                                </div>
                                {isSelected && (
                                  <div className="text-emerald-500">
                                    <Check size={16} />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* INFO PANEL — slide-in depuis la droite */}
            <aside
              className={`
                absolute top-0 right-0 bottom-0 w-72 border-l flex flex-col z-20
                transition-transform duration-200 ease-in-out
                ${showInfoPanel ? "translate-x-0" : "translate-x-full"}
                ${isDarkMode ? "bg-gray-950 border-gray-800" : "bg-white border-gray-200"}
              `}
            >
              <div className={`flex items-center justify-between px-4 py-3 border-b flex-shrink-0 ${
                isDarkMode ? "border-gray-800" : "border-gray-200"
              }`}>
                <span className="text-sm font-semibold">Session info</span>
                <button
                  onClick={() => setShowInfoPanel(false)}
                  className={`p-1.5 rounded-lg border transition ${
                    isDarkMode ? "border-gray-700 hover:bg-gray-800" : "border-gray-200 hover:bg-gray-50"
                  }`}
                  title="Close (Esc)"
                >
                  <X size={13} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-5">
                <div>
                  <label className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>
                    Session name
                  </label>
                  <div className={`mt-1.5 rounded-lg border px-3 py-2 text-sm select-none ${
                    isDarkMode
                      ? "bg-gray-900 border-gray-800 text-gray-500 cursor-not-allowed"
                      : "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed"
                  }`}>
                    {name.trim() || <span className="italic text-[13px]">No name yet</span>}
                  </div>
                  <p className={`text-[10px] mt-1 ${muted}`}>
                    Edit the name directly in the header above.
                  </p>
                </div>

                <div>
                  <label className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value.slice(0, 300))}
                    rows={6}
                    className={`w-full mt-1.5 rounded-lg border px-3 py-2 text-sm outline-none resize-none transition-colors ${inputClass}`}
                    placeholder="Session objectives, context, target intensity…"
                  />
                  <p className={`text-right text-[10px] mt-1 ${muted}`}>
                    {description.length} / 300
                  </p>
                </div>

                <div className={`h-px ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`} />
              </div>

              <div className={`p-4 border-t flex-shrink-0 ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}>
                <button
                  onClick={() => setShowInfoPanel(false)}
                  className="w-full py-2 rounded-xl text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition"
                >
                  Done
                </button>
              </div>
            </aside>

          </div>
        </div>
      </div>
    </>
  );
}